import ColumnFilter, { SortableHeader } from "@/components/ColumnFilter";
import { ArrowDown, ArrowUp, ArrowUUpLeft, ArrowUUpRight, ArrowsLeftRight, FilePdf, Signature, Paperclip } from "@phosphor-icons/react";

const TYPE_LABEL = { entry: "Entrata", exit: "Uscita", return: "Reso", transfer: "Trasferimento", scarico_commessa: "Scarico Commessa", reso_fornitore: "Reso a Fornitore", recupero_commessa: "Recupero Commessa" };
const TYPE_STYLES = {
  entry: "bg-emerald-50 text-emerald-700 border-emerald-200",
  exit: "bg-red-50 text-red-700 border-red-200",
  return: "bg-amber-50 text-amber-700 border-amber-200",
  transfer: "bg-blue-50 text-blue-700 border-blue-200",
  scarico_commessa: "bg-purple-50 text-purple-700 border-purple-200",
  reso_fornitore: "bg-orange-50 text-orange-700 border-orange-200",
  recupero_commessa: "bg-teal-50 text-teal-700 border-teal-200",
};
const TYPE_ICON = { entry: ArrowDown, exit: ArrowUp, return: ArrowUUpLeft, transfer: ArrowsLeftRight, scarico_commessa: ArrowUp, reso_fornitore: ArrowUUpRight, recupero_commessa: ArrowUUpLeft };

export function effectiveSupplier(r) {
  if (!r) return "";
  if (r.type === "entry") {
    return r.entry_supplier_name || r.product_supplier_name || "";
  }
  if (Array.isArray(r.bulk_allocations) && r.bulk_allocations.length) {
    const names = [...new Set(r.bulk_allocations.map((a) => a.supplier_name).filter(Boolean))];
    if (names.length) return names.join(" · ");
  }
  return r.product_supplier_name || "";
}

/**
 * Movements list (desktop table + mobile cards). Pure presentational component
 * driven by props — it receives the already-grouped rows, column filter state,
 * sort state and callbacks for signing / downloading PDFs / opening detail.
 */
export default function MovementListTable({
  rows,                 // raw rows for mobile cards
  filteredRows,         // grouped+filtered+sorted rows for desktop table
  uniques,
  colFilters,
  setColFilter,
  sortCfg,
  setSortCfg,
  onRowClick,
  onSign,
  onDownloadPdf,
}) {
  return (
    <>
      {/* Desktop table */}
      <div className="hidden md:block bg-white border border-neutral-200 overflow-auto max-h-[calc(100vh-280px)]">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 border-b border-neutral-200 sticky top-0 z-20 shadow-sm">
            <tr className="text-left">
              <th className="px-4 py-3 label-eyebrow bg-neutral-50">
                <span className="inline-flex items-center gap-1">
                  <SortableHeader testid="sort-m-date" label="Data" sortKey="created_at" sortConfig={sortCfg} setSortConfig={setSortCfg} />
                  <ColumnFilter testid="filter-m-date" values={uniques.created_at_day} selected={colFilters.created_at_day} onChange={setColFilter("created_at_day")} />
                </span>
              </th>
              <th className="px-4 py-3 label-eyebrow bg-neutral-50">
                <span className="inline-flex items-center gap-1">
                  <SortableHeader testid="sort-m-type" label="Tipo" sortKey="type" sortConfig={sortCfg} setSortConfig={setSortCfg} />
                  <ColumnFilter testid="filter-m-type" values={uniques.type} selected={colFilters.type} onChange={setColFilter("type")} />
                </span>
              </th>
              <th className="px-4 py-3 label-eyebrow bg-neutral-50">
                <span className="inline-flex items-center gap-1">
                  <SortableHeader testid="sort-m-product" label="Prodotto" sortKey="product_name" sortConfig={sortCfg} setSortConfig={setSortCfg} />
                  <ColumnFilter testid="filter-m-product" values={uniques.product_name} selected={colFilters.product_name} onChange={setColFilter("product_name")} />
                </span>
              </th>
              <th className="px-4 py-3 label-eyebrow bg-neutral-50">
                <span className="inline-flex items-center gap-1">
                  <SortableHeader testid="sort-m-serials" label="Seriali" sortKey="serial_flat" sortConfig={sortCfg} setSortConfig={setSortCfg} />
                  <ColumnFilter testid="filter-m-serials" values={uniques.serial_flat} selected={colFilters.serial_flat} onChange={setColFilter("serial_flat")} />
                </span>
              </th>
              <th className="px-4 py-3 label-eyebrow bg-neutral-50">
                <span className="inline-flex items-center gap-1">
                  <SortableHeader testid="sort-m-supplier" label="Fornitore" sortKey="product_supplier_name" sortConfig={sortCfg} setSortConfig={setSortCfg} />
                  <ColumnFilter testid="filter-m-supplier" values={uniques.product_supplier_name} selected={colFilters.product_supplier_name} onChange={setColFilter("product_supplier_name")} />
                </span>
              </th>
              <th className="px-4 py-3 label-eyebrow text-right bg-neutral-50">
                <SortableHeader testid="sort-m-qty" label="Qtà" sortKey="quantity" sortConfig={sortCfg} setSortConfig={setSortCfg} align="right" />
              </th>
              <th className="px-4 py-3 label-eyebrow bg-neutral-50">
                <span className="inline-flex items-center gap-1">
                  <SortableHeader testid="sort-m-from" label="Da" sortKey="from_warehouse_name" sortConfig={sortCfg} setSortConfig={setSortCfg} />
                  <ColumnFilter testid="filter-m-from" values={uniques.from_warehouse_name} selected={colFilters.from_warehouse_name} onChange={setColFilter("from_warehouse_name")} />
                  <span className="text-neutral-400">→</span>
                  <SortableHeader testid="sort-m-to" label="A" sortKey="to_warehouse_name" sortConfig={sortCfg} setSortConfig={setSortCfg} />
                  <ColumnFilter testid="filter-m-to" values={uniques.to_warehouse_name} selected={colFilters.to_warehouse_name} onChange={setColFilter("to_warehouse_name")} />
                </span>
              </th>
              <th className="px-4 py-3 label-eyebrow bg-neutral-50">
                <span className="inline-flex items-center gap-1">
                  <SortableHeader testid="sort-m-dest" label="Destinatario / DDT" sortKey="destination" sortConfig={sortCfg} setSortConfig={setSortCfg} />
                  <ColumnFilter testid="filter-m-dest" values={uniques.destination} selected={colFilters.destination} onChange={setColFilter("destination")} align="right" />
                </span>
              </th>
              <th className="px-4 py-3 label-eyebrow bg-neutral-50">
                <span className="inline-flex items-center gap-1">
                  <SortableHeader testid="sort-m-user" label="Utente" sortKey="created_by" sortConfig={sortCfg} setSortConfig={setSortCfg} />
                  <ColumnFilter testid="filter-m-user" values={uniques.created_by} selected={colFilters.created_by} onChange={setColFilter("created_by")} align="right" />
                </span>
              </th>
              <th className="px-4 py-3 label-eyebrow text-right bg-neutral-50">PDF</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 ? (
              <tr><td colSpan="10" className="px-4 py-12 text-center text-neutral-500">Nessun movimento.</td></tr>
            ) : filteredRows.map((r) => {
              const Icon = TYPE_ICON[r.type];
              let destLabel = null;
              if (r.customer_name) destLabel = `👤 ${r.customer_name}`;
              else if (r.commessa_name) destLabel = `📍 ${r.commessa_name}`;
              else if (r.supplier_dest_name) destLabel = `🏭 ${r.supplier_dest_name}`;
              return (
                <tr key={r.id} data-testid={`mov-row-${r.id}`} onClick={() => onRowClick(r.id)} title="Clicca per dettagli" className="border-b border-neutral-100 hover:bg-[#002FA7]/5 transition-colors cursor-pointer">
                  <td className="px-4 py-3 text-neutral-600 font-mono text-xs">{new Date(r.created_at).toLocaleString("it-IT")}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs border ${TYPE_STYLES[r.type]}`}>
                      <Icon size={12} weight="bold" /> {TYPE_LABEL[r.type]}
                    </span>
                  </td>
                  <td className="px-4 py-3 min-w-[260px] max-w-[420px]">
                    <div className="font-medium whitespace-normal break-words leading-snug">
                      {r.product_name}
                      {r.group_size > 1 && (
                        <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border border-[#002FA7] text-[#002FA7] bg-[#002FA7]/5" title={r.group_children_products?.map(p => `${p.sku} — ${p.name} (${p.qty} ${p.uom})`).join('\n')}>
                          +{r.group_size - 1} prod.
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-neutral-500 font-mono mt-0.5">
                      {r.group_size > 1 ? `${r.product_sku} + ${r.group_size - 1} altri prodotti` : r.product_sku}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs font-mono max-w-[220px]">
                    {(() => {
                      const serials = r.group_size > 1 ? (r.group_all_serials || []) : (r.product_is_serialized ? (r.serial_numbers || []) : []);
                      if (serials.length === 0) return <span className="text-neutral-300">—</span>;
                      return (
                        <div className="flex flex-wrap gap-1" title={serials.join(", ")}>
                          {serials.slice(0, 3).map((sn) => (
                            <span key={sn} className="inline-block px-1.5 py-0.5 bg-neutral-100 border border-neutral-200 text-[10px]">{sn}</span>
                          ))}
                          {serials.length > 3 && (
                            <span className="inline-block px-1.5 py-0.5 text-[10px] text-neutral-500">+{serials.length - 3}</span>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3 text-xs text-neutral-600" data-testid={`mov-supplier-${r.id}`}>{effectiveSupplier(r) || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold" data-testid={`mov-row-qty-${r.id}`}>
                    {r.group_size > 1 ? (
                      <span title={`Somma di ${r.group_size} prodotti`}>{r.group_total_qty ?? r.quantity}</span>
                    ) : (
                      r.quantity
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <span className="text-neutral-600">{r.from_warehouse_name || "—"}</span>
                    <span className="mx-1 text-neutral-400">→</span>
                    <span className="text-neutral-900 font-medium">{r.to_warehouse_name || "—"}</span>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {destLabel && <div className="font-medium">{destLabel}</div>}
                    {r.reference && <div className="text-neutral-500 mt-0.5">{r.type === "entry" ? "DDT: " : "Rif: "}{r.reference}</div>}
                    {!destLabel && !r.reference && <span className="text-neutral-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-neutral-700 whitespace-nowrap" data-testid={`mov-user-${r.id}`} title={r.created_by || ""}>{r.created_by_name || r.created_by || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      {!r.signatures?.destinatario && (
                        <button
                          data-testid={`sign-${r.id}`}
                          onClick={() => onSign(r)}
                          title="Acquisisci firma destinatario"
                          className="p-1.5 text-amber-600 hover:bg-amber-50 transition-colors"
                        >
                          <Signature size={16} weight="bold" />
                        </button>
                      )}
                      {r.signatures?.destinatario && (
                        <span
                          data-testid={`signed-${r.id}`}
                          title="Firmato dal destinatario"
                          className="p-1.5 text-emerald-600"
                        >
                          <Signature size={16} weight="bold" />
                        </span>
                      )}
                      {r.signed_scan && (
                        <span
                          data-testid={`mov-scan-badge-${r.id}`}
                          title={`Scansione firmata caricata${r.signed_scan.uploaded_at ? " il " + new Date(r.signed_scan.uploaded_at).toLocaleString("it-IT") : ""}`}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] uppercase tracking-wider border border-emerald-500 text-emerald-700 bg-emerald-50"
                        >
                          <Paperclip size={10} weight="bold" />
                          Scan
                        </span>
                      )}
                      <button data-testid={`pdf-${r.id}`} onClick={() => onDownloadPdf(r)} className="p-1.5 hover:bg-[#002FA7] hover:text-white transition-colors" title="Scarica PDF">
                        <FilePdf size={16} weight="bold" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-2">
        {rows.length === 0 ? (
          <div className="bg-white border border-neutral-200 p-8 text-center text-sm text-neutral-500">Nessun movimento.</div>
        ) : rows.map((r) => {
          const Icon = TYPE_ICON[r.type];
          return (
            <div key={r.id} data-testid={`mov-card-${r.id}`} onClick={() => onRowClick(r.id)} className="bg-white border border-neutral-200 p-4 cursor-pointer hover:border-[#002FA7]">
              <div className="flex items-center justify-between mb-2">
                <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs border ${TYPE_STYLES[r.type]}`}>
                  <Icon size={12} weight="bold" /> {TYPE_LABEL[r.type]}
                </span>
                <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                  {r.signed_scan && (
                    <span
                      title="Scansione firmata caricata"
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] uppercase tracking-wider border border-emerald-500 text-emerald-700 bg-emerald-50"
                    >
                      <Paperclip size={10} weight="bold" />
                      Scan
                    </span>
                  )}
                  <button onClick={() => onDownloadPdf(r)} className="p-1.5 border border-neutral-200 hover:bg-[#002FA7] hover:text-white transition-colors">
                    <FilePdf size={14} weight="bold" />
                  </button>
                </div>
              </div>
              <div className="font-medium text-sm">
                {r.product_name}
                {r.group_size > 1 && (
                  <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border border-[#002FA7] text-[#002FA7] bg-[#002FA7]/5">
                    +{r.group_size - 1} prod.
                  </span>
                )}
              </div>
              <div className="text-xs text-neutral-500 font-mono mb-2">
                {r.product_sku} · Qtà {r.group_size > 1 ? (r.group_total_qty ?? r.quantity) : r.quantity}
                {r.group_size > 1 ? ` (${r.group_size} prodotti)` : ""}
              </div>
              {effectiveSupplier(r) && (
                <div className="text-xs text-neutral-500 mb-1">Fornitore: {effectiveSupplier(r)}</div>
              )}
              {r.product_is_serialized && r.serial_numbers && r.serial_numbers.length > 0 && (
                <div className="text-xs mb-1 flex flex-wrap gap-1">
                  <span className="text-neutral-500 mr-1">Seriali:</span>
                  {r.serial_numbers.map((sn) => (
                    <span key={sn} className="inline-block px-1.5 py-0.5 bg-neutral-100 border border-neutral-200 font-mono text-[10px]">{sn}</span>
                  ))}
                </div>
              )}
              <div className="text-xs text-neutral-600 mb-1">
                <span>{r.from_warehouse_name || "—"}</span> → <span className="font-medium">{r.to_warehouse_name || "—"}</span>
              </div>
              {r.customer_name && <div className="text-xs text-neutral-800 mt-1">👤 {r.customer_name}</div>}
              {r.commessa_name && <div className="text-xs text-neutral-800 mt-1">📍 {r.commessa_name}</div>}
              {r.supplier_dest_name && <div className="text-xs text-neutral-800 mt-1">🏭 {r.supplier_dest_name}</div>}
              <div className="text-xs text-neutral-500 font-mono mt-1">{new Date(r.created_at).toLocaleString("it-IT")}</div>
              {(r.created_by_name || r.created_by) && <div className="text-xs text-neutral-600 mt-0.5" title={r.created_by || ""}>👤 {r.created_by_name || r.created_by}</div>}
              {r.reference && <div className="text-xs text-neutral-600 mt-1">{r.type === "entry" ? "DDT" : "Rif"}: {r.reference}</div>}
            </div>
          );
        })}
      </div>
    </>
  );
}
